import React, { forwardRef } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { analyticsReportQuery, sendAnalyticsReport } from '../../../../graph/dashboard';
import useModal from '../../../../hooks/useModal';
import { MenuItem } from '@material-ui/core';
import DetailedReportModal from './DetailedReportModal';
import objectHash from 'object-hash/dist/object_hash';
import { pickFilter } from '../GeneralAnalytics';

interface DetailedReportButtonProps {
  pdf?: { label: string; value: string };
  filter: any;
  onBusy: () => () => void;
  thresholds: any;
}

const DetailedReportButton = forwardRef<HTMLLIElement, DetailedReportButtonProps>(
  ({ pdf, filter, onBusy, thresholds }, ref) => {
    const client = useApolloClient();
    const [reportQueryWorking, setReportQueryWorking] = React.useState<boolean>(false);
    const [lastDetailedReport, setLastDetailedReport] = React.useState<any>(null);
    const { open, openModal, closeModal } = useModal();
    const [sendReport] = useMutation(sendAnalyticsReport);

    const { dateRange, operationRooms, physicians, ...rest } = filter || {};

    const sanitizedOperationRoomsFilter =
      Array.isArray(operationRooms) && operationRooms.length > 0
        ? { operationRooms: operationRooms.map(or => parseInt(or, 10)) }
        : isFinite(parseInt(operationRooms, 10))
          ? { operationRooms: [parseInt(operationRooms, 10)] }
          : {};

    const sanitizedPhysiciansFilter =
      Array.isArray(physicians) && physicians.length > 0
        ? { physicians: physicians.map(py => parseInt(py, 10)) }
        : isFinite(parseInt(physicians, 10))
          ? { physicians: [parseInt(physicians, 10)] }
          : {};

    return (
      <>
        <MenuItem
          ref={ref}
          disabled={open}
          onClick={async event => {
            if (event.altKey) {
              openModal();
            } else {
              if (reportQueryWorking) {
                return;
              }

              const filterHash = objectHash(JSON.parse(JSON.stringify(filter)));

              if (lastDetailedReport !== null && lastDetailedReport[0] === filterHash) {
                window.open(lastDetailedReport[1], '_blank');
              } else {
                setReportQueryWorking(true);
                const onDone = onBusy();

                try {
                  const result = await client.query({
                    query: analyticsReportQuery,
                    variables: {
                      filter: {
                        dateRange: dateRange,
                        ...sanitizedOperationRoomsFilter,
                        ...sanitizedPhysiciansFilter,
                        ...pickFilter(rest),
                      },
                      pdf: pdf?.value ?? '',
                      ...thresholds,
                    },
                  });

                  setLastDetailedReport([filterHash, result.data.detailedReport]);
                  window.open(result.data.detailedReport, '_blank');
                } finally {
                  setReportQueryWorking(false);
                  onDone();
                }
              }

              closeModal();
            }
          }}
        >
          {pdf?.label ?? 'Detailed Report'}
        </MenuItem>

        {open && <DetailedReportModal onBusy={onBusy} onClose={() => closeModal()} />}
      </>
    );
  }
);

DetailedReportButton.displayName = 'DetailedReportButton';

export default DetailedReportButton;
